const COVERAGE_STATUS_LABELS = {
  COMPLETE: 'Tam geçmiş',
  PARTIAL: 'Kısmi geçmiş',
  BOUNDARY_ONLY: 'Yalnız kapsam sınırı',
  UNAVAILABLE: 'Geçmiş kullanılamıyor',
}

const COVERAGE_STATUS_COLORS = {
  COMPLETE: 'success',
  PARTIAL: 'warning',
  BOUNDARY_ONLY: 'warning',
  UNAVAILABLE: 'error',
}

const COVERAGE_SOURCE_LABELS = {
  APPLE_TRANSACTION_HISTORY: 'Apple Transaction History',
  APPLE_TRANSACTION_HISTORY_V2: 'Apple Transaction History v2',
  APPLE_APP_STORE_SERVER_API: 'Apple App Store Server API',
  GOOGLE_RTDN_INBOX: 'Google RTDN inbox',
  GOOGLE_RTDN_ORDERS: 'Google RTDN + Orders API',
  GOOGLE_PROCESSED_RTDN_ORDERS: 'Google işlenmiş RTDN + Orders API',
  GOOGLE_CURRENT_ORDER: 'Google doğrulanmış güncel order',
  GOOGLE_ORDERS_API: 'Google Orders API',
  GOOGLE_FINANCIAL_ORDERS: 'Google finansal raporları + Orders API',
  GOOGLE_VERIFIED_ORDER_HISTORY: 'Google doğrulanmış order geçmişi',
  LEGACY_BOUNDARY: 'Legacy HISTORY_BOUNDARY',
  LEGACY_HISTORY_BOUNDARY: 'Legacy HISTORY_BOUNDARY',
  LEGACY_CURRENT_SNAPSHOT: 'Legacy güncel store snapshot’ı',
  MIXED: 'Birleştirilmiş authoritative kaynaklar',
}

const PERIOD_STATUS_LABELS = {
  SUCCEEDED: 'Başarılı',
  RENEWAL_SUCCEEDED: 'Yenileme başarılı',
  FAILED: 'Başarısız',
  RENEWAL_FAILED: 'Yenileme başarısız',
  PAYMENT_FAILED: 'Ödeme başarısız',
  GRACE_STARTED: 'Grace',
  ON_HOLD_STARTED: 'On hold',
  PAYMENT_RECOVERED: 'Ödeme kurtarıldı',
  REFUNDED: 'İade',
  REFUND: 'İade',
  REVOKED: 'Geri alındı',
  REVOCATION: 'Geri alındı',
}

function normalized(value) {
  return String(value || '').toUpperCase()
}

function withoutRawLineage(value = {}) {
  const safeValue = { ...value }
  delete safeValue.lineageRef
  delete safeValue.subscriptionLineageRef

  return safeValue
}

function normalizeCoverage(coverage = {}) {
  return {
    ...withoutRawLineage(coverage),
    status: coverage.status || coverage.historyStatus || coverage.completeness,
    source: coverage.source || coverage.historySource,
    coverageStartAt: coverage.coverageStartAt ?? coverage.completeFrom,
    coverageEndAt: coverage.coverageEndAt ?? coverage.completeUntil,
    missingBeforeAt: coverage.missingBeforeAt ?? coverage.missingFrom,
    missingUntilAt: coverage.missingUntilAt ?? coverage.missingUntil,
    missingDescription: coverage.missingDescription || coverage.missingRangeDescription,
    importedAt: coverage.importedAt ?? coverage.lastImportedAt,
    cursor: coverage.cursor || coverage.providerCursor || coverage.sourceCursor,
    revision: coverage.revision || coverage.providerRevision || coverage.sourceRevision,
    lineageRefMasked: coverage.lineageRefMasked
      || coverage.lineageRefHash
      || coverage.lineageHash
      || coverage.nonReversibleLineageRef,
    verifiedTransactionCount: coverage.verifiedTransactionCount
      ?? coverage.verifiedTransactions,
  }
}

function normalizeSummary(summary = {}) {
  const safeSummary = withoutRawLineage(summary)
  if (safeSummary.historyCoverage) {
    safeSummary.historyCoverage = normalizeCoverage(safeSummary.historyCoverage)
  }
  if (Array.isArray(safeSummary.historyCoverages)) {
    safeSummary.historyCoverages = safeSummary.historyCoverages.map(normalizeCoverage)
  }
  if (Array.isArray(safeSummary.coverages)) {
    safeSummary.coverages = safeSummary.coverages.map(normalizeCoverage)
  }

  return safeSummary
}

function normalizePeriod(period = {}) {
  return {
    ...withoutRawLineage(period),
    sequence: period.sequence ?? period.periodOrdinal,
    renewalOrdinal: period.renewalOrdinal,
    sequenceLabel: period.sequenceLabel || period.label,
    transactionType: period.transactionType || period.eventType,
    transactionStatus: period.transactionStatus || period.status,
  }
}

function normalizeTimelineItem(item = {}) {
  return withoutRawLineage(item)
}

function resolveHistoryCoverageStatus(summary = {}, coverageRecords = []) {
  const explicitStatus = summary.historyCoverageStatus || summary.historyCoverage?.status
  if (explicitStatus) {
    return normalized(explicitStatus)
  }

  const statuses = coverageRecords
    .map(coverage => normalized(coverage?.status))
    .filter(Boolean)
  if (statuses.length) {
    if (statuses.every(status => status === 'COMPLETE')) {
      return 'COMPLETE'
    }
    if (statuses.every(status => status === 'UNAVAILABLE')) {
      return 'UNAVAILABLE'
    }
    if (statuses.includes('PARTIAL') || statuses.includes('COMPLETE')) {
      return 'PARTIAL'
    }
    if (statuses.includes('BOUNDARY_ONLY')) {
      return 'BOUNDARY_ONLY'
    }

    return statuses[0]
  }

  // The legacy field is a lower bound, not proof that provider pagination was exhausted.
  return summary.historyCompleteFrom ? 'BOUNDARY_ONLY' : 'UNAVAILABLE'
}

function isHistoryComplete(status, summary = {}, coverageRecords = []) {
  return normalized(status) === 'COMPLETE'
    && normalized(summary.renewalCountSemantics) === 'LIFETIME'
    && coverageRecords.length > 0
    && coverageRecords.every(coverage => (
      normalized(coverage?.status) === 'COMPLETE'
      && coverage?.complete === true
    ))
}

function coverageStatusLabel(value) {
  return COVERAGE_STATUS_LABELS[normalized(value)] || value || 'Geçmiş bilinmiyor'
}

function coverageStatusColor(value) {
  return COVERAGE_STATUS_COLORS[normalized(value)] || 'primary'
}

function coverageSourceLabel(value) {
  return COVERAGE_SOURCE_LABELS[normalized(value)] || value || '-'
}

function coverageRangeLabel(coverage = {}, formatTimestamp, legacyBoundary) {
  if (normalized(coverage.status) === 'BOUNDARY_ONLY') {
    return `Sınır: ${formatTimestamp(coverage.missingBeforeAt || coverage.coverageStartAt || legacyBoundary)}`
  }
  if (coverage.coverageStartAt || coverage.coverageEndAt) {
    return `${formatTimestamp(coverage.coverageStartAt)} → ${formatTimestamp(coverage.coverageEndAt)}`
  }

  return '-'
}

function missingCoverageLabel(coverage = {}, formatTimestamp) {
  if (coverage.missingDescription) {
    if (coverage.missingDescription === 'Provider history before the recorded boundary has not been proven complete') {
      return `${formatTimestamp(coverage.missingBeforeAt || coverage.coverageStartAt)} öncesi provider geçmişi tam doğrulanmadı`
    }

    return coverage.missingDescription
  }
  if (Array.isArray(coverage.missingRanges) && coverage.missingRanges.length) {
    return coverage.missingRanges.map(range => {
      if (typeof range === 'string') {
        return range
      }

      return `${formatTimestamp(range?.from)} → ${formatTimestamp(range?.to)}`
    }).join(', ')
  }
  if (coverage.missingBeforeAt) {
    return `${formatTimestamp(coverage.missingBeforeAt)} öncesi doğrulanmadı`
  }
  if (coverage.missingUntilAt) {
    return `${formatTimestamp(coverage.missingUntilAt)} tarihine kadar doğrulanmadı`
  }
  if (normalized(coverage.status) === 'COMPLETE') {
    return 'Yok'
  }

  return 'Eksik aralık provider kanıtından belirlenemedi'
}

function compactIdentifier(value) {
  const valueAsText = String(value || '')
  if (valueAsText.length <= 24) {
    return valueAsText || '-'
  }

  return `${valueAsText.slice(0, 10)}…${valueAsText.slice(-8)}`
}

function recordedPeriodLabel(period = {}) {
  const movementType = normalized(period.transactionType || period.eventType)
  if (movementType === 'STORE_ORDER') return 'Mağazada doğrulanmış işlem'
  if (['RENEWAL', 'RENEWAL_SUCCEEDED'].includes(movementType)) {
    return 'Doğrulanmış yenileme'
  }
  if (['PURCHASE', 'SUBSCRIPTION_STARTED'].includes(movementType)) {
    return 'Doğrulanmış satın alım'
  }
  if (['TRIAL_CONVERSION', 'TRIAL_CONVERTED'].includes(movementType)) {
    return 'Doğrulanmış trial dönüşümü'
  }
  if (['RESUBSCRIPTION', 'SUBSCRIPTION_RESTARTED'].includes(movementType)) {
    return 'Doğrulanmış yeniden abonelik'
  }
  if (movementType === 'TRIAL_STARTED') {
    return 'Doğrulanmış trial başlangıcı'
  }

  return 'Doğrulanmış store dönemi'
}

function periodMarkerLabel(period = {}, index = 0, historyComplete = false) {
  if (!historyComplete) {
    return '•'
  }

  return period.sequence || period.periodOrdinal || index + 1
}

function periodSequenceLabel(period = {}, historyComplete = false) {
  if (period.transactionType === 'STORE_ORDER') return 'Mağazada doğrulanmış işlem'
  if (!historyComplete) {
    return recordedPeriodLabel(period)
  }
  if (period.sequenceLabel) {
    return period.sequenceLabel
  }
  if (period.transactionType === 'PURCHASE') {
    return 'İlk satın alım'
  }
  if (period.transactionType === 'TRIAL_CONVERSION') {
    return 'İlk ücretli dönem (deneme dönüşümü)'
  }
  if (period.transactionType === 'RENEWAL') {
    // Never invent a lifetime ordinal from a partial list.
    return period.renewalOrdinal ? `${period.renewalOrdinal}. yenileme` : 'Doğrulanmış yenileme'
  }
  if (period.transactionType === 'RESUBSCRIPTION') {
    return 'Yeniden abonelik'
  }
  if (period.eventType === 'TRIAL_STARTED') {
    return 'Deneme başlangıcı'
  }

  return null
}

function timelineSequenceLabel(item = {}, historyComplete = false) {
  if (historyComplete) {
    return item.sequenceLabel || null
  }
  if (item.sequenceLabel || item.periodOrdinal || item.renewalOrdinal) {
    return recordedPeriodLabel(item)
  }

  return null
}

function periodStatusValue(period = {}) {
  return normalized(period.transactionStatus || period.status || period.eventType)
}

function periodStatusLabel(period = {}) {
  const value = periodStatusValue(period)

  return PERIOD_STATUS_LABELS[value] || value || '-'
}

function periodStatusColor(period = {}) {
  const value = periodStatusValue(period)
  if (['SUCCEEDED', 'RENEWAL_SUCCEEDED', 'PAYMENT_RECOVERED'].includes(value)) {
    return 'success'
  }
  if (['GRACE_STARTED', 'ON_HOLD_STARTED'].includes(value)) {
    return 'warning'
  }
  if (['FAILED', 'RENEWAL_FAILED', 'PAYMENT_FAILED', 'REFUNDED', 'REFUND', 'REVOKED', 'REVOCATION'].includes(value)) {
    return 'error'
  }

  return 'primary'
}

module.exports = {
  compactIdentifier,
  coverageRangeLabel,
  coverageSourceLabel,
  coverageStatusColor,
  coverageStatusLabel,
  isHistoryComplete,
  missingCoverageLabel,
  normalizeCoverage,
  normalizePeriod,
  normalizeSummary,
  normalizeTimelineItem,
  periodMarkerLabel,
  periodSequenceLabel,
  periodStatusColor,
  periodStatusLabel,
  resolveHistoryCoverageStatus,
  timelineSequenceLabel,
}
